import {
    AlertCircle,
    Building,
    Calendar,
    CheckCircle,
    Clock,
    Edit,
    Eye,
    FileText,
    MapPin,
    Plus,
    RefreshCw,
    Search,
    Send,
    User2,
    Users,
    X,
    XCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { api, centerApi, CreateMobilisationRequest, Mobilisation, mobilisationApi, Program, programsApi, User, userApi } from "../api";
import { useAuth } from "../hooks/useAuth";
import ModalPortal from "./ModalPortal";


// interface Center {
//     center_id: number;
//     name: string;
//     city: string;
//     capacity: number;
// }

const isNonEmptyString = (value?: string | null) => typeof value === "string" && value.trim().length > 0;
const toNumberOrNull = (value?: number | string | null) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") {
        return Number.isNaN(value) ? null : value;
    }
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
};
const isValidNumber = (value?: number | string | null) => toNumberOrNull(value) !== null;
const hasAnyPlacementGroup = (
    company?: string | null,
    jobRole?: string | null,
    placementType?: string | null,
    expectedSalary?: number | string | null
) =>
    isNonEmptyString(company) ||
    isNonEmptyString(jobRole) ||
    isNonEmptyString(placementType) ||
    isValidNumber(expectedSalary);
const isPlacementGroupComplete = (
    company?: string | null,
    jobRole?: string | null,
    placementType?: string | null,
    expectedSalary?: number | string | null
) =>
    isNonEmptyString(company) &&
    isNonEmptyString(jobRole) &&
    isNonEmptyString(placementType) &&
    isValidNumber(expectedSalary);
const getPlacementGroupError = (
    label: string,
    company?: string | null,
    jobRole?: string | null,
    placementType?: string | null,
    expectedSalary?: number | string | null
) => {
    if (!hasAnyPlacementGroup(company, jobRole, placementType, expectedSalary)) return null;
    if (isPlacementGroupComplete(company, jobRole, placementType, expectedSalary)) return null;
    return `All placement fields are required for ${label} when any are provided.`;
};

const MobilisationManagement: React.FC = () => {
    const { user } = useAuth();
    const [mobilisations, setMobilisations] = useState<Mobilisation[]>([]);
    // const [centers, setCenters] = useState<Center[]>([]);
    const [mpCenterAdmins, setMpCenterAdmins] = useState<User[]>([]);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [mobilisationPartners, setMobilisationPartners] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    // const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [selectedMobilisation, setSelectedMobilisation] = useState<Mobilisation | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [mpFilter, setMpFilter] = useState<number | "">("");
    const [reviewComments, setReviewComments] = useState('');
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState("details"); // 'details' or 'history'
    const [showAllApprovals, setShowAllApprovals] = useState(false);
    const [editingMobilisation, setEditingMobilisation] = useState<CreateMobilisationRequest | null>(null);
    const [formData, setFormData] = useState<CreateMobilisationRequest>({
        title: "",
        description: "",
        state: "",
        district: "",
        city: "",
        program_id: 0,
        placement_company: "",
        placement_job_role: "",
        expected_salary: 0,
        placement_company_2: "",
        placement_job_role_2: "",
        expected_salary_2: null,
        placement_type_2: "",
        placement_company_3: "",
        placement_job_role_3: "",
        expected_salary_3: null,
        placement_type_3: "",
        placement_company_4: "",
        placement_job_role_4: "",
        expected_salary_4: null,
        placement_type_4: "",
        placement_company_5: "",
        placement_job_role_5: "",
        expected_salary_5: null,
        placement_type_5: "",
        candidates_count: 0,
        proposed_batch_launch_date: "",
        proposed_placement_drive_date: "",
        center_admin_id: "",
        placement_type: "",
        local_trainer_identified: "",
        mobilisation_id: 0
    });

    const isSystemOrSuperAdmin = user?.role === "SYSTEM_ADMIN" || user?.role === "SUPER_ADMIN";
    const isSystemOrOperationsAdmin = user?.role === "SYSTEM_ADMIN" || user?.role === "OPERATIONS_ADMIN";

    useEffect(() => {
        handleRefresh()
    }, [statusFilter, user, mpFilter]);

    const handleRefresh = () => {
        fetchMobilisations();
        // fetchCenters();
        if((user?.role === "SYSTEM_ADMIN" || user?.role === "OPERATIONS_ADMIN" || user?.role === "SUPER_ADMIN") || user?.role === "MOBILISATION_PARTNER_ADMIN" ){
            fetchPrograms();
            fetchMPCenterAdmins();
        }
        if (isSystemOrSuperAdmin) {
            fetchMobilisationPartners();
        }
    };

    const fetchMobilisations = async () => {
        try {
            setLoading(true);
            setError(null);
            setLoading(true);
            const response = await mobilisationApi.getMobilisations({
                page: 1,
                limit: 999999,
                // queryStatus: user?.role == "SYSTEM_ADMIN" ? 'Submitted' : statusFilter,
                queryStatus: statusFilter,
                mp_id: isSystemOrSuperAdmin && mpFilter ? Number(mpFilter) : undefined,
            });

            if (response.success && response.data) {
                const data = response.data;
                const scopedData =
                    user?.role === "CENTER_ADMIN" || user?.role === "PROJECT_MANAGER"
                        ? data.filter((item) => item.center_admin_id === user.user_id)
                        : data;
                setMobilisations(scopedData);
            } else {
                setError(response.message || "Failed to fetch mobilisations");
            }
        } catch (error) {
            console.error("Failed to fetch mobilisations:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPrograms = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await programsApi.getPrograms({
                page: 1,
                limit: 9999999,
                queryStatus: 'Active'
            });
            if (response.success && response.data) {
                setPrograms(response.data);
            } else {
                setError(response.message || "Failed to fetch programs");
            }
        } catch (error: any) {
            console.error("Failed to fetch programs:", error);
            setError(error.response?.data?.message || "Failed to fetch programs.");
        } finally {
            setLoading(false);
        }
    }

    // const fetchCenters = async () => {
    //     try {
    //         setLoading(true);
    //         setError(null);
    //         const response = await centerApi.getCenters({
    //             page: 1,
    //             limit: 9999999,
    //         });
    //         if (response.success && response.data) {
    //             setCenters(response.data);
    //         } else {
    //             setError(response.message || "Failed to fetch centers");
    //         }
    //     } catch (error) {
    //         console.error("Failed to fetch centers:", error);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const fetchMPCenterAdmins = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await userApi.getUsers({
                page: 1,
                limit: 9999999,
                queryRole: "CENTER_ADMIN",
                queryMpId: user?.mp_id
            });
            if (response.success && response.data) {
                setMpCenterAdmins(response.data);
            } else {
                setError(response.message || "Failed to fetch center admins");
            }
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to fetch center admins.")
            console.error("Failed to fetch center admins:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMobilisationPartners = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/partners');
            if (response.status == 200 && response.data) {
                setMobilisationPartners(response.data.data);
            } else {
                setError(response.statusText || 'Failed to fetch users');
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            setError(error.response?.data?.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEditMobilisation = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setError(null);
            setSubmitLoading(true);

            const placementGroupError =
                getPlacementGroupError(
                    "Company 2",
                    formData.placement_company_2,
                    formData.placement_job_role_2,
                    formData.placement_type_2,
                    formData.expected_salary_2
                ) ||
                getPlacementGroupError(
                    "Company 3",
                    formData.placement_company_3,
                    formData.placement_job_role_3,
                    formData.placement_type_3,
                    formData.expected_salary_3
                ) ||
                getPlacementGroupError(
                    "Company 4",
                    formData.placement_company_4,
                    formData.placement_job_role_4,
                    formData.placement_type_4,
                    formData.expected_salary_4
                ) ||
                getPlacementGroupError(
                    "Company 5",
                    formData.placement_company_5,
                    formData.placement_job_role_5,
                    formData.placement_type_5,
                    formData.expected_salary_5
                );

            if (placementGroupError) {
                setError(placementGroupError);
                setSubmitLoading(false);
                return;
            }

            const trimmed = {
                ...formData,
                title: formData.title?.trim(),
                city: formData.city?.trim(),
                district: formData.district?.trim(),
                state: formData.state?.trim(),
                placement_company: formData.placement_company?.trim(),
                placement_job_role: formData.placement_job_role?.trim(),
                placement_company_2: isNonEmptyString(formData.placement_company_2)
                    ? formData.placement_company_2?.trim()
                    : null,
                placement_job_role_2: isNonEmptyString(formData.placement_job_role_2)
                    ? formData.placement_job_role_2?.trim()
                    : null,
                expected_salary_2: toNumberOrNull(formData.expected_salary_2),
                placement_type_2: isNonEmptyString(formData.placement_type_2) ? formData.placement_type_2 : null,
                placement_company_3: isNonEmptyString(formData.placement_company_3)
                    ? formData.placement_company_3?.trim()
                    : null,
                placement_job_role_3: isNonEmptyString(formData.placement_job_role_3)
                    ? formData.placement_job_role_3?.trim()
                    : null,
                expected_salary_3: toNumberOrNull(formData.expected_salary_3),
                placement_type_3: isNonEmptyString(formData.placement_type_3) ? formData.placement_type_3 : null,
                placement_company_4: isNonEmptyString(formData.placement_company_4)
                    ? formData.placement_company_4?.trim()
                    : null,
                placement_job_role_4: isNonEmptyString(formData.placement_job_role_4)
                    ? formData.placement_job_role_4?.trim()
                    : null,
                expected_salary_4: toNumberOrNull(formData.expected_salary_4),
                placement_type_4: isNonEmptyString(formData.placement_type_4) ? formData.placement_type_4 : null,
                placement_company_5: isNonEmptyString(formData.placement_company_5)
                    ? formData.placement_company_5?.trim()
                    : null,
                placement_job_role_5: isNonEmptyString(formData.placement_job_role_5)
                    ? formData.placement_job_role_5?.trim()
                    : null,
                expected_salary_5: toNumberOrNull(formData.expected_salary_5),
                placement_type_5: isNonEmptyString(formData.placement_type_5) ? formData.placement_type_5 : null,
                // keep other fields as-is (dates/numbers/arrays)
            };

            if (editingMobilisation) {
                const mid = trimmed.mobilisation_id ? trimmed.mobilisation_id.toString() : '';
                const response = await mobilisationApi.updateMobilisation(mid, trimmed);

                if (response.data && response.success) {
                    setShowCreateModal(false);
                    fetchMobilisations();
                    resetForm();
                    setSuccess("Mobilisation updated successfully!");
                }
            }
            else {
                const response = await mobilisationApi.createMobilisation(trimmed);

                if (response.data && response.success) {
                    setShowCreateModal(false);
                    fetchMobilisations();
                    resetForm();
                    setSuccess("Mobilisation created successfully!");
                }
            }
        } catch (error: any) {
            console.error(editingMobilisation ? "Failed to update mobilisation:" : "Failed to create mobilisation:", error);
            setError(error?.response?.data?.message || (editingMobilisation ? "Failed to update mobilisation" : "Failed to create mobilisation"));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSubmitForReview = async (id: number) => {
        try {
            setError(null);
            setLoading(true);

            const response = await api.post(`/mobilisations/${id}/submit`);

            if (response.data.success) {
                fetchMobilisations();
                setSuccess("Mobilisation submitted for review successfully!");
            }
        } catch (error: any) {
            console.error("Failed to submit mobilisation:", error);
            setError(error?.response?.data?.message ?? "Failed to submit mobilisation");
        } finally {
            setLoading(false);
        }
    };

    const openEditForm = (mobilisation: Mobilisation) => {
        setError(null);
        setSubmitLoading(false);
        setFormData({
            title: mobilisation.title,
            description: mobilisation.description,
            state: mobilisation.state,
            district: mobilisation.district,
            city: mobilisation.city,
            placement_company: mobilisation.placement_company,
            placement_job_role: mobilisation.placement_job_role,
            expected_salary: mobilisation.expected_salary,
            placement_type: mobilisation.placement_type,
            placement_company_2: mobilisation.placement_company_2 ?? "",
            placement_job_role_2: mobilisation.placement_job_role_2 ?? "",
            expected_salary_2: mobilisation.expected_salary_2 ?? null,
            placement_type_2: mobilisation.placement_type_2 ?? "",
            placement_company_3: mobilisation.placement_company_3 ?? "",
            placement_job_role_3: mobilisation.placement_job_role_3 ?? "",
            expected_salary_3: mobilisation.expected_salary_3 ?? null,
            placement_type_3: mobilisation.placement_type_3 ?? "",
            placement_company_4: mobilisation.placement_company_4 ?? "",
            placement_job_role_4: mobilisation.placement_job_role_4 ?? "",
            expected_salary_4: mobilisation.expected_salary_4 ?? null,
            placement_type_4: mobilisation.placement_type_4 ?? "",
            placement_company_5: mobilisation.placement_company_5 ?? "",
            placement_job_role_5: mobilisation.placement_job_role_5 ?? "",
            expected_salary_5: mobilisation.expected_salary_5 ?? null,
            placement_type_5: mobilisation.placement_type_5 ?? "",
            candidates_count: mobilisation.candidates_count,
            proposed_batch_launch_date: mobilisation.proposed_batch_launch_date,
            proposed_placement_drive_date: mobilisation.proposed_placement_drive_date,
            center_admin_id: mobilisation.center_admin_id,
            program_id: mobilisation.program_id,
            local_trainer_identified: mobilisation.local_trainer_identified,
            mobilisation_id: mobilisation.mobilisation_id
        });
        setEditingMobilisation(mobilisation);
        setShowCreateModal(true);
    };

    const handleReviewMobilisation = async (id: number, action: "Approved" | "Rejected") => {
        if (!selectedMobilisation) return;
        try {
            setError(null);
            setLoading(true);

            await api.post(`/mobilisations/${id}/review`, {
                action,
                comments: reviewComments,
            });
            setSuccess(`Mobilisation has been ${action}.`);
            setReviewComments('');
            setShowViewModal(false);
            fetchMobilisations();
        } catch (error: any) {
            setError(error.response?.data?.message || `Failed to update mobilisation status to ${action}.`);
            console.log(`Failed to set mobilisation status to ${action}`, error);
        } finally {
            setLoading(false);
        }
    };


    // const handleStartRegistration = async (id: number, endDate: string) => {
    //     try {
    //         const response = await api.post(`/mobilisations/${id}/start-registration`, {
    //             registration_end_date: endDate
    //         });

    //         if (response.data.success) {
    //             fetchMobilisations();
    //             setShowRegistrationModal(false);
    //         }
    //     } catch (error) {
    //         console.error('Failed to start registration:', error);
    //     }
    // };

    const formatDateToYYYYMMDD = (dateInput: any) => {
        if (!dateInput) return '';

        let date;
        // Check if dateInput is already a Date object
        if (dateInput instanceof Date) {
            date = dateInput;
        } else {
            // Try to parse it as a string
            try {
                date = new Date(dateInput);
            } catch (error) {
                console.error("Invalid date input:", dateInput, error);
                return '';
            }
        }

        // Ensure the date is valid before formatting
        if (isNaN(date.getTime())) {
            console.warn("Invalid Date object after parsing:", dateInput);
            return '';
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            state: "",
            district: "",
            city: "",
            program_id: 0,
            placement_company: "",
            placement_job_role: "",
            expected_salary: 0,
            placement_company_2: "",
            placement_job_role_2: "",
            expected_salary_2: null,
            placement_type_2: "",
            placement_company_3: "",
            placement_job_role_3: "",
            expected_salary_3: null,
            placement_type_3: "",
            placement_company_4: "",
            placement_job_role_4: "",
            expected_salary_4: null,
            placement_type_4: "",
            placement_company_5: "",
            placement_job_role_5: "",
            expected_salary_5: null,
            placement_type_5: "",
            candidates_count: 0,
            proposed_batch_launch_date: "",
            proposed_placement_drive_date: "",
            center_admin_id: "",
            placement_type: "",
            local_trainer_identified: "",
            mobilisation_id: 0
        });
        setSubmitLoading(false);
        setError(null);
    };

    // const canStartRegistration = (mobilisation: Mobilisation) => {
    //     return mobilisation.status === 'Approved' && !mobilisation.registration_start_date;
    // };

    // const isRegistrationActive = (mobilisation: Mobilisation) => {
    //     if (
    //         !mobilisation.registration_start_date ||
    //         !mobilisation.registration_end_date
    //     )
    //         return false;
    //     const now = new Date();
    //     const endDate = new Date(mobilisation.registration_end_date);
    //     return now <= endDate;
    // };

    const filteredMobilisations = mobilisations.filter(
        (mobilisation) =>
            mobilisation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            mobilisation.city.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Approved':
                return <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
            case 'Rejected':
                return <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
            case 'Pending':
                return <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9.247a4.5 4.5 0 000 5.506 4.5 4.5 0 010-5.506zm0 0l-1.583 1.583m1.583-1.583L9.811 7.664m-1.583 1.583l-1.583-1.583m1.583 1.583L9.811 7.664m1.583-1.583L12 6.5l-1.583-1.583m1.583 1.583l-1.583-1.583M12 6.5l1.583 1.583m-1.583-1.583L10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
            default:
                return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Approved':
                return 'bg-green-100 text-green-800';
            case 'Rejected':
                return 'bg-red-100 text-red-800';
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    const isModalOpen = showCreateModal || showViewModal;
    const company2Required = hasAnyPlacementGroup(
        formData.placement_company_2,
        formData.placement_job_role_2,
        formData.placement_type_2,
        formData.expected_salary_2
    );
    const company3Required = hasAnyPlacementGroup(
        formData.placement_company_3,
        formData.placement_job_role_3,
        formData.placement_type_3,
        formData.expected_salary_3
    );
    const company4Required = hasAnyPlacementGroup(
        formData.placement_company_4,
        formData.placement_job_role_4,
        formData.placement_type_4,
        formData.expected_salary_4
    );
    const company5Required = hasAnyPlacementGroup(
        formData.placement_company_5,
        formData.placement_job_role_5,
        formData.placement_type_5,
        formData.expected_salary_5
    );
    const hasCompany2Placement = selectedMobilisation
        ? hasAnyPlacementGroup(
            selectedMobilisation.placement_company_2,
            selectedMobilisation.placement_job_role_2,
            selectedMobilisation.placement_type_2,
            selectedMobilisation.expected_salary_2 ?? null
        )
        : false;
    const hasCompany3Placement = selectedMobilisation
        ? hasAnyPlacementGroup(
            selectedMobilisation.placement_company_3,
            selectedMobilisation.placement_job_role_3,
            selectedMobilisation.placement_type_3,
            selectedMobilisation.expected_salary_3 ?? null
        )
        : false;
    const hasCompany4Placement = selectedMobilisation
        ? hasAnyPlacementGroup(
            selectedMobilisation.placement_company_4,
            selectedMobilisation.placement_job_role_4,
            selectedMobilisation.placement_type_4,
            selectedMobilisation.expected_salary_4 ?? null
        )
        : false;
    const hasCompany5Placement = selectedMobilisation
        ? hasAnyPlacementGroup(
            selectedMobilisation.placement_company_5,
            selectedMobilisation.placement_job_role_5,
            selectedMobilisation.placement_type_5,
            selectedMobilisation.expected_salary_5 ?? null
        )
        : false;

    return (
        // className="space-y-6"
        <div >
            {/* Notifications */}
            {error && !isModalOpen && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setError(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{success}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setSuccess(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Mobilisation Management
                    </h1>
                    <p className="text-gray-600">
                        Manage training mobilisation proposals and approvals
                    </p>
                </div>
                {user?.role == "MOBILISATION_PARTNER_ADMIN" && (
                    <button
                        onClick={() => { setError(null); resetForm(); setEditingMobilisation(null); setSubmitLoading(false); setShowCreateModal(true); }}
                        className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        New Mobilisation
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search mobilisations..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    {isSystemOrSuperAdmin && (
                        <div className="flex gap-4">
                            <select
                                id="mp-filter"
                                className="input"
                                value={mpFilter}
                                onChange={(e) =>
                                    setMpFilter(e.target.value ? Number(e.target.value) : "")
                                }
                            >
                                <option value="">All Partners</option>
                                {mobilisationPartners.map((mp) => (
                                    <option key={mp.mp_id} value={mp.mp_id}>
                                        {mp.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {user?.role == "MOBILISATION_PARTNER_ADMIN" && (
                        <div className="sm:w-48">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                                <option value="">All Status</option>
                                <option value="Draft">Draft</option>
                                <option value="Submitted">Submitted</option>
                                {/* <option value="UnderReview">Under Review</option> */}
                                <option value="Approved">Approved</option>
                                <option value="Rejected">Rejected</option>
                                {/* <option value="RevisionRequired">Revision Required</option> */}
                            </select>
                        </div>
                    )}
                    <button
                        onClick={handleRefresh}
                        className="btn-secondary flex items-center"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Mobilisations Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredMobilisations.map((mobilisation) => {
                    const remainingRevisions = Math.max(
                        0,
                        (mobilisation.max_revisions ?? 0) - (mobilisation.revision_count ?? 0)
                    );

                    return (
                        <div
                        key={mobilisation.mobilisation_id}
                        className="h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                                    {mobilisation.title}
                                </h3>
                                <div className="flex items-center gap-2">
                                    {getStatusIcon(mobilisation.status)}
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                            mobilisation.status
                                        )}`}
                                    >
                                        {mobilisation.status}
                                    </span>
                                </div>
                                {/* {isRegistrationActive(mobilisation) && (
                                    <div className="mt-2">
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Registration Open
                                        </span>
                                    </div>
                                )} */}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="space-y-3 mb-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Building className="w-4 h-4" />
                                <span>{mobilisation.program.name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <MapPin className="w-4 h-4" />
                                <span>
                                    {mobilisation.city}, {mobilisation.district},{" "}
                                    {mobilisation.state}
                                </span>
                            </div>
                            {mobilisation.proposed_batch_launch_date && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Calendar className="w-4 h-4" />
                                    <span>
                                        Proposed Launch Date:{" "}
                                        {new Date(
                                            mobilisation.proposed_batch_launch_date
                                        ).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                            {mobilisation.candidates_count && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Users className="w-4 h-4" />
                                    <span>
                                        {mobilisation.candidates_count} candidates
                                    </span>
                                </div>
                            )}
                            {mobilisation.center_admin.full_name && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <User2 className="w-4 h-4" />
                                    <span>Center Admin: {mobilisation.center_admin.full_name}</span>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="mt-auto flex flex-col gap-2 pt-2">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setSelectedMobilisation(mobilisation);
                                        setShowViewModal(true)
                                    }}
                                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 flex items-center justify-center gap-1"
                                >
                                    <Eye className="w-4 h-4" />
                                    View
                                </button>
                                {user?.role === "MOBILISATION_PARTNER_ADMIN" &&
                                    ["Draft", "Rejected"].includes(mobilisation.status) &&
                                    user.mp_id === mobilisation.mp_id && (
                                        <>
                                            <button
                                                onClick={() => openEditForm(mobilisation)}
                                                className="flex-1 bg-primary-600 text-white px-3 py-2 rounded hover:bg-primary-700 flex items-center justify-center gap-1"
                                                title="Edit"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleSubmitForReview(mobilisation.mobilisation_id)
                                                }
                                                disabled={remainingRevisions === 0}
                                                className={`flex-1 px-3 py-2 rounded flex items-center justify-center gap-1 ${
                                                    remainingRevisions === 0
                                                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                                        : "bg-primary-600 text-white hover:bg-primary-700"
                                                }`}
                                                title="Submit for Approval"
                                            >
                                                <Send className="w-4 h-4" />
                                                Submit
                                            </button>
                                        </>
                                    )}
                            </div>
                            {user?.role === "MOBILISATION_PARTNER_ADMIN" && mobilisation.status != 'Approved' && (
                                <p className="text-xs text-gray-500 text-center">
                                    {remainingRevisions > 0
                                        ? `${remainingRevisions} revision${remainingRevisions === 1 ? "" : "s"} remaining`
                                        : "No revisions remaining"}
                                </p>
                            )}

                            {/* {canStartRegistration(mobilisation) && (
                                <button
                                    onClick={() => {
                                        setSelectedMobilisation(mobilisation);
                                        setShowRegistrationModal(true);
                                    }}
                                    className="w-full bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 flex items-center justify-center gap-1"
                                >
                                    <Users className="w-4 h-4" />
                                    Start Registration
                                </button>
                            )}

                            {isRegistrationActive(mobilisation) && (
                                <button
                                    onClick={() =>
                                        window.open(
                                            `/mobilisations/${mobilisation.mobilisation_id}/registrations`,
                                            "_blank"
                                        )
                                    }
                                    className="w-full bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                                >
                                    <FileText className="w-4 h-4" />
                                    Manage Registrations
                                </button>
                            )} */}
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Empty State */}
            {/* {filteredMobilisations.length === 0 && (
                <div className="text-center py-12">
                    <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No mobilisations found
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Get started by creating your first mobilisation proposal.
                    </p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
                    >
                        Create Mobilisation
                    </button>
                </div>
            )} */}

            {/* Create/ edit Modal */}
            {showCreateModal && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                {editingMobilisation ? "Edit Mobilisation" : "Create New Mobilisation"}
                            </h2>
                        </div>

                        {error && showCreateModal && (
                            <div className="mx-6 mt-4 mb-2 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <span className="flex-1">{error}</span>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() => setError(null)}
                                    aria-label="Dismiss error"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        <form
                            onSubmit={handleCreateEditMobilisation}
                            onChangeCapture={() => setError(null)}
                            className="p-6 space-y-6"
                        >
                            {/* Basic Information */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Basic Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Title *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.title}
                                            onChange={(e) =>
                                                setFormData({ ...formData, title: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                    {/* <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Program Name *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.program_name}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    program_name: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div> */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={formData.description}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    description: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Location */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Location
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.state}
                                            onChange={(e) =>
                                                setFormData({ ...formData, state: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            District *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.district}
                                            onChange={(e) =>
                                                setFormData({ ...formData, district: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            City *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.city}
                                            onChange={(e) =>
                                                setFormData({ ...formData, city: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Mobilisation Partner Selection - Only for System Admins */}
                            {/* {user?.role === "SYSTEM_ADMIN" && (
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        Mobilisation Partner
                                    </h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Select Partner (Optional)
                                        </label>
                                        <select
                                            value={formData.mp_id}
                                            onChange={(e) =>
                                                setFormData({ ...formData, mp_id: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        >
                                            <option value="">Choose a partner...</option>
                                            {mobilisationPartners.map((partner) => (
                                                <option key={partner.mp_id} value={partner.mp_id}>
                                                    {partner.name} -{" "}
                                                    {partner.contact_email || partner.contact_phone}
                                                </option>
                                            ))}
                                        </select>
                                        {mobilisationPartners.length === 0 && (
                                            <p className="text-sm text-amber-600 mt-1">
                                                No mobilisation partners available. The system will
                                                assign the first available partner automatically.
                                            </p>
                                        )}
                                        {mobilisationPartners.length > 0 && (
                                            <p className="text-sm text-gray-500 mt-1">
                                                Leave empty to automatically assign the first available
                                                partner.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )} */}

                            {/* Center Selection */}
                            {/* <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Training Center
                                </h3>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Select Center
                                    </label>
                                    <select
                                        value={formData.center_id}
                                        onChange={(e) =>
                                            setFormData({ ...formData, center_id: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="">Choose a center...</option>
                                        {centers.map((center) => (
                                            <option key={center.center_id} value={center.center_id}>
                                                {center.name} - {center.city} (Capacity:{" "}
                                                {center.capacity})
                                            </option>
                                        ))}
                                    </select>
                                    {centers.length === 0 && (
                                        <p className="text-sm text-amber-600 mt-1">
                                            No approved centers available. Please create and get
                                            approval for a center first.
                                        </p>
                                    )}
                                </div>
                            </div> */}

                            {/* Center Admin Selection */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Center Admin Selection
                                </h3>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Select Center Admin *
                                    </label>
                                    <select
                                        value={formData.center_admin_id}
                                        onChange={(e) =>
                                            setFormData({ ...formData, center_admin_id: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="">Choose a center admin...</option>
                                        {mpCenterAdmins.map((ca) => (
                                            <option key={ca.user_id} value={ca.user_id}>
                                                {ca.full_name}
                                            </option>
                                        ))}
                                    </select>
                                    {mpCenterAdmins.length === 0 && (
                                        <p className="text-sm text-amber-600 mt-1">
                                            No center admins available. Please create a center admin first.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Program Details */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Program Details
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Program Name *
                                        </label>
                                        <select
                                            value={formData.program_id}
                                            onChange={(e) =>
                                                setFormData({ ...formData, program_id: parseInt(e.target.value) })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        >
                                            <option value="">Choose a program...</option>
                                            {programs.map((p) => (
                                                <option key={p.program_id} value={p.program_id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Candidates Count *
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.candidates_count}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    candidates_count: parseInt(e.target.value),
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Placement Requirements */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Placement Requirements
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Target Company *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.placement_company}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    placement_company: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Job Role *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.placement_job_role}
                                            onChange={(e) =>
                                                setFormData({ ...formData, placement_job_role: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Placement Type *
                                        </label>
                                        <select
                                            value={formData.placement_type}
                                            onChange={(e) =>
                                                setFormData({ ...formData, placement_type: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        >
                                            <option value="">Choose a placement type...</option>
                                            <option value="OnRole">OnRole</option>
                                            <option value="OffRole">OffRole</option>
                                            <option value="Internship">Internship</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Expected Salary (₹) *
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.expected_salary}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    expected_salary: parseFloat(e.target.value),
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h4 className="text-base font-medium text-gray-900 mb-3">Company 2</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Target Company 2{company2Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_company_2 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        placement_company_2: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Job Role 2{company2Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_job_role_2 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_job_role_2: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Placement Type 2{company2Required ? " *" : ""}
                                            </label>
                                            <select
                                                value={formData.placement_type_2 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_type_2: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            >
                                                <option value="">Choose a placement type...</option>
                                                <option value="OnRole">OnRole</option>
                                                <option value="OffRole">OffRole</option>
                                                <option value="Internship">Internship</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Expected Salary (₹) 2{company2Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.expected_salary_2 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        expected_salary_2:
                                                            e.target.value === "" ? null : parseFloat(e.target.value),
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h4 className="text-base font-medium text-gray-900 mb-3">Company 3</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Target Company 3{company3Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_company_3 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        placement_company_3: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Job Role 3{company3Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_job_role_3 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_job_role_3: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Placement Type 3{company3Required ? " *" : ""}
                                            </label>
                                            <select
                                                value={formData.placement_type_3 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_type_3: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            >
                                                <option value="">Choose a placement type...</option>
                                                <option value="OnRole">OnRole</option>
                                                <option value="OffRole">OffRole</option>
                                                <option value="Internship">Internship</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Expected Salary (₹) 3{company3Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.expected_salary_3 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        expected_salary_3:
                                                            e.target.value === "" ? null : parseFloat(e.target.value),
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h4 className="text-base font-medium text-gray-900 mb-3">Company 4</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Target Company 4{company4Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_company_4 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        placement_company_4: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Job Role 4{company4Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_job_role_4 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_job_role_4: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Placement Type 4{company4Required ? " *" : ""}
                                            </label>
                                            <select
                                                value={formData.placement_type_4 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_type_4: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            >
                                                <option value="">Choose a placement type...</option>
                                                <option value="OnRole">OnRole</option>
                                                <option value="OffRole">OffRole</option>
                                                <option value="Internship">Internship</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Expected Salary (₹) 4{company4Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.expected_salary_4 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        expected_salary_4:
                                                            e.target.value === "" ? null : parseFloat(e.target.value),
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h4 className="text-base font-medium text-gray-900 mb-3">Company 5</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Target Company 5{company5Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_company_5 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        placement_company_5: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Job Role 5{company5Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.placement_job_role_5 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_job_role_5: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Placement Type 5{company5Required ? " *" : ""}
                                            </label>
                                            <select
                                                value={formData.placement_type_5 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, placement_type_5: e.target.value })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            >
                                                <option value="">Choose a placement type...</option>
                                                <option value="OnRole">OnRole</option>
                                                <option value="OffRole">OffRole</option>
                                                <option value="Internship">Internship</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Expected Salary (₹) 5{company5Required ? " *" : ""}
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.expected_salary_5 ?? ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        expected_salary_5:
                                                            e.target.value === "" ? null : parseFloat(e.target.value),
                                                    })
                                                }
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Timeline
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Proposed Batch Launch Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formatDateToYYYYMMDD(formData.proposed_batch_launch_date)}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    proposed_batch_launch_date: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Proposed Placement Drive Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formatDateToYYYYMMDD(formData.proposed_placement_drive_date)}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    proposed_placement_drive_date: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Trainer */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Local Trainer Identified? *
                                </label>
                                <select
                                    value={formData.local_trainer_identified}
                                    onChange={(e) =>
                                        setFormData({ ...formData, local_trainer_identified: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    <option value="">Choose a status...</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                    <option value="WIP">WIP</option>
                                </select>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <span className="flex items-center gap-2">
                                        {submitLoading && showCreateModal && (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                        )}
                                        {submitLoading && showCreateModal ? (editingMobilisation ? 'Updating...' : 'Creating...') : (editingMobilisation ? 'Update Mobilisation' : 'Create Mobilisation')}
                                    </span>
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* View Modal */}
            {showViewModal && selectedMobilisation && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-sans">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative p-5 border">
                        {/* Header Section */}
                        <div className="flex justify-between items-start pb-4 border-b border-gray-200">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">{selectedMobilisation.title}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    {getStatusIcon(selectedMobilisation.status)}
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedMobilisation.status)}`}>
                                        {selectedMobilisation.status}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                &times; {/* Unicode multiplication sign for a clear 'x' */}
                            </button>
                        </div>

                        {/* Tabs for Details and Approval History */}
                        <div className="border-b border-gray-200 mb-4">
                            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                                <button
                                    onClick={() => setSelectedTab("details")}
                                    className={`${selectedTab === "details"
                                        ? "border-indigo-500 text-indigo-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                        } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                                >
                                    Details
                                </button>
                                <button
                                    onClick={() => setSelectedTab("history")}
                                    className={`${selectedTab === "history"
                                        ? "border-indigo-500 text-indigo-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                        } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                                >
                                    History
                                </button>
                            </nav>
                        </div>

                        {/* Content based on selected tab */}
                        {selectedTab === "details" && (
                            <div className="space-y-6 pb-5"> {/* Added pb-5 for consistent padding */}
                                {/* Basic Information */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Description</span>
                                            <p className="text-gray-900">{selectedMobilisation.description || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Program</span>
                                            <p className="text-gray-900">{selectedMobilisation.program.name}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Location</span>
                                            <p className="text-gray-900">{selectedMobilisation.city}, {selectedMobilisation.district}, {selectedMobilisation.state}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Placement</span>
                                            <p className="text-gray-900">{selectedMobilisation.placement_company} / {selectedMobilisation.placement_job_role} / {selectedMobilisation.placement_type}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Salary</span>
                                            <p className="text-gray-900">₹{selectedMobilisation.expected_salary}</p>
                                        </div>
                                        {hasCompany2Placement && (
                                            <>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Placement 2</span>
                                                    <p className="text-gray-900">
                                                        {selectedMobilisation.placement_company_2} / {selectedMobilisation.placement_job_role_2} / {selectedMobilisation.placement_type_2}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Salary 2</span>
                                                    <p className="text-gray-900">₹{selectedMobilisation.expected_salary_2}</p>
                                                </div>
                                            </>
                                        )}
                                        {hasCompany3Placement && (
                                            <>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Placement 3</span>
                                                    <p className="text-gray-900">
                                                        {selectedMobilisation.placement_company_3} / {selectedMobilisation.placement_job_role_3} / {selectedMobilisation.placement_type_3}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Salary 3</span>
                                                    <p className="text-gray-900">₹{selectedMobilisation.expected_salary_3}</p>
                                                </div>
                                            </>
                                        )}
                                        {hasCompany4Placement && (
                                            <>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Placement 4</span>
                                                    <p className="text-gray-900">
                                                        {selectedMobilisation.placement_company_4} / {selectedMobilisation.placement_job_role_4} / {selectedMobilisation.placement_type_4}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Salary 4</span>
                                                    <p className="text-gray-900">₹{selectedMobilisation.expected_salary_4}</p>
                                                </div>
                                            </>
                                        )}
                                        {hasCompany5Placement && (
                                            <>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Placement 5</span>
                                                    <p className="text-gray-900">
                                                        {selectedMobilisation.placement_company_5} / {selectedMobilisation.placement_job_role_5} / {selectedMobilisation.placement_type_5}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">Salary 5</span>
                                                    <p className="text-gray-900">₹{selectedMobilisation.expected_salary_5}</p>
                                                </div>
                                            </>
                                        )}
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Candidates</span>
                                            <p className="text-gray-900">{selectedMobilisation.candidates_count}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Proposed Launch Date</span>
                                            <p className="text-gray-900">{" "}
                                                {new Date(
                                                    selectedMobilisation.proposed_batch_launch_date
                                                ).toLocaleDateString()}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Proposed Placement Drive Date</span>
                                            <p className="text-gray-900">{" "}
                                                {new Date(
                                                    selectedMobilisation.proposed_placement_drive_date
                                                ).toLocaleDateString()}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Center Admin</span>
                                            <p className="text-gray-900">{selectedMobilisation.center_admin.full_name}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Center Admin Email</span>
                                            <p className="text-gray-900">{selectedMobilisation.center_admin.email}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Center Admin Phone</span>
                                            <p className="text-gray-900">{selectedMobilisation.center_admin.phone}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Local Trainer Identified</span>
                                            <p className="text-gray-900">{selectedMobilisation.local_trainer_identified}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Rejection Reason - Show ONLY if rejected */}
                                {selectedMobilisation.status === 'Rejected' && (
                                    <div className="mt-4">
                                        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
                                            <XCircle className="text-red-400 w-5 h-5 flex-shrink-0 mt-1" />
                                            <div>
                                                <div className="text-sm font-semibold text-red-700">
                                                    Rejection Reason
                                                </div>
                                                <div className="text-sm text-red-700">
                                                    {selectedMobilisation.approval_history &&
                                                        selectedMobilisation.approval_history.length > 0
                                                        ? selectedMobilisation.approval_history.find(
                                                            (a) => a.action === "Rejected"
                                                        )?.comments || "No comment provided"
                                                        : "No comment provided"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}


                                {/* Approval/Rejection Actions */}
                                {isSystemOrOperationsAdmin && selectedMobilisation.status === 'Submitted' && (
                                    <div className="mt-6 border-t border-gray-200 pt-6">
                                        <h4 className="text-sm font-medium text-gray-500 mb-2">
                                            Review Actions
                                        </h4>
                                        <textarea
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm mb-3"
                                            rows={3}
                                            placeholder="Add comments for approval or rejection..."
                                            value={reviewComments}
                                            onChange={(e) => setReviewComments(e.target.value)}
                                        ></textarea>
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => handleReviewMobilisation(selectedMobilisation.mobilisation_id, 'Approved')}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150 ease-in-out"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                            </button>
                                            <button
                                                onClick={() => handleReviewMobilisation(selectedMobilisation.mobilisation_id, 'Rejected')}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150 ease-in-out"
                                            >
                                                <XCircle className="w-4 h-4 mr-2" /> Reject
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedTab === "history" && (
                            <div className="space-y-4 pb-5"> {/* Added pb-5 for consistent padding */}
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Approval History</h3>
                                {selectedMobilisation.approval_history?.length ? (
                                    <div className="divide-y divide-gray-200">
                                        {selectedMobilisation.approval_history
                                            .slice(
                                                0,
                                                showAllApprovals
                                                    ? selectedMobilisation.approval_history.length
                                                    : 3
                                            )
                                            .map((entry, idx) => (
                                                <div key={idx} className="py-3">
                                                    <div className="flex items-center justify-between">
                                                        <span
                                                            className={`font-medium ${entry.action === "Approved"
                                                                ? "text-green-600"
                                                                : entry.action === "Rejected"
                                                                    ? "text-red-600"
                                                                    : "text-gray-800"
                                                                }`}
                                                        >
                                                            {entry.action}
                                                        </span>
                                                        <span className="text-xs text-gray-500">
                                                            {formatDateTime(entry.reviewed_at)}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-700 mt-1">
                                                        {entry.comments || "No comments provided."}
                                                    </p>
                                                </div>
                                            ))}
                                        {selectedMobilisation.approval_history.length > 3 && (
                                            <div className="text-center pt-3">
                                                <button
                                                    onClick={() => setShowAllApprovals(!showAllApprovals)}
                                                    className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                                >
                                                    {showAllApprovals ? "Show Less" : "Show All"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">No history available.</p>
                                )}
                            </div>
                        )}
                        </div>
                    </div>
                </ModalPortal>
            )}


            {/* Registration Modal
            {showRegistrationModal && selectedMobilisation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Start Registration Period
                            </h2>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target as HTMLFormElement);
                                const endDate = formData.get("endDate") as string;
                                if (endDate && selectedMobilisation) {
                                    handleStartRegistration(
                                        selectedMobilisation.mobilisation_id,
                                        endDate
                                    );
                                }
                            }}
                            className="p-6 space-y-4"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Registration End Date *
                                </label>
                                <input
                                    type="date"
                                    name="endDate"
                                    required
                                    min={new Date().toISOString().split("T")[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                                <p className="text-sm text-gray-600 mt-1">
                                    Registration will start immediately and remain open until this
                                    date.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowRegistrationModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    Start Registration
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )} */}
        </div >
    );
};

export default MobilisationManagement;
